/* Mock orders — list, get, cancel, reorder, track. Timeline status is
 * derived from the order's age so a freshly-placed demo order visibly
 * progresses through stages while the demo runs. */

import * as store from "@/data/mocks/storage";
import * as cart from "./cart";
import { withLatency, withError, paginate, pushAudit, getSession } from "./_util";

const STAGE_LABELS = {
  placed:           "Order placed",
  packed:           "Packed at warehouse",
  shipped:          "Shipped",
  out_for_delivery: "Out for delivery",
  delivered:        "Delivered",
  cancelled:        "Cancelled",
};

const STAGE_NOTES = {
  placed:           "We've received your order",
  packed:           "Palayamkottai WH-01 · batch traceable",
  shipped:          "Picked up by Delhivery",
  out_for_delivery: "Rider en route",
  delivered:        "Handed off at the registered address",
  cancelled:        "Order cancelled at customer request",
};

/** Derive current status + timeline from the order's placed_at timestamp. */
export function deriveTimeline(order) {
  if (!order) return { status: "placed", timeline: [] };
  if (order.cancelled_at) {
    return {
      status: "cancelled",
      timeline: [
        { code: "placed",    label: STAGE_LABELS.placed,    ts: order.placed_at,    done: true, note: STAGE_NOTES.placed },
        { code: "cancelled", label: STAGE_LABELS.cancelled, ts: order.cancelled_at, done: true, note: STAGE_NOTES.cancelled },
      ],
    };
  }
  const placedTs = new Date(order.placed_at).getTime();
  const ageMin = (Date.now() - placedTs) / 60000;
  let stage = "placed";
  if (ageMin >= 360) stage = "delivered";
  else if (ageMin >= 60) stage = "out_for_delivery";
  else if (ageMin >= 10) stage = "shipped";
  else if (ageMin >= 2)  stage = "packed";

  const order_stages = ["placed", "packed", "shipped", "out_for_delivery", "delivered"];
  const stageOffsetMin = { placed: 0, packed: 2, shipped: 10, out_for_delivery: 60, delivered: 360 };
  const idx = order_stages.indexOf(stage);
  const timeline = order_stages.map((code, i) => {
    const done = i <= idx;
    const ts = new Date(placedTs + stageOffsetMin[code] * 60000).toISOString();
    return { code, label: STAGE_LABELS[code], ts, done, note: STAGE_NOTES[code] };
  });
  return { status: stage, timeline };
}

function readOrders() {
  return store.get("orders", []) || [];
}

function writeOrders(arr) {
  store.set("orders", arr);
}

function visibleToCurrentUser(o) {
  const session = store.get("session", null);
  if (!session) return o.user_id === "anon";
  // Admin / manager / sales see everything; users see their own.
  if (["admin", "manager", "sales"].includes(session.role)) return true;
  return o.user_id === session.user_id;
}

export async function list(params = {}) {
  let all = readOrders().filter(visibleToCurrentUser);
  if (params.status) all = all.filter((o) => o.status === params.status);
  if (params.q) {
    const q = String(params.q).toLowerCase();
    all = all.filter((o) => (o.number || "").toLowerCase().includes(q));
  }
  // Sort latest first.
  all = all.slice().sort((a, b) => new Date(b.placed_at) - new Date(a.placed_at));
  return withLatency(paginate(all.map(decorate), params));
}

export async function get(number) {
  const order = readOrders().find((o) => o.number === number);
  if (!order) return withError(`Order ${number} not found`, 404);
  return withLatency(decorate(order));
}

export async function cancel(number, { reason } = {}) {
  const all = readOrders();
  const idx = all.findIndex((o) => o.number === number);
  if (idx === -1) return withError(`Order ${number} not found`, 404);
  const order = all[idx];
  if (order.status === "delivered") return withError("Delivered orders cannot be cancelled", 400);
  if (order.cancelled_at) return withError("Order already cancelled", 400);
  const updated = { ...order, cancelled_at: new Date().toISOString(), cancel_reason: reason || null };
  all[idx] = updated;
  writeOrders(all);
  return withLatency(decorate(updated));
}

export async function reorder(number) {
  const order = readOrders().find((o) => o.number === number);
  if (!order) return withError(`Order ${number} not found`, 404);
  for (const it of order.items) {
    await cart.add({ slug: it.product_slug, qty: it.qty });
  }
  return withLatency({ ok: true, items_added: order.items.length });
}

export async function track(number) {
  const order = readOrders().find((o) => o.number === number);
  if (!order) return withError(`Order ${number} not found`, 404);
  return withLatency(decorate(order));
}

function decorate(order) {
  const { status, timeline } = deriveTimeline(order);
  return {
    ...order,
    status,
    timeline,
    eta:
      status === "delivered"
        ? "Delivered"
        : status === "cancelled"
          ? "Cancelled"
          : `Expected within ${stageEtaLabel(status)}`,
  };
}

function stageEtaLabel(stage) {
  switch (stage) {
    case "placed":            return "10 minutes";
    case "packed":            return "1 hour";
    case "shipped":           return "today";
    case "out_for_delivery":  return "the next few hours";
    default:                  return "soon";
  }
}

/* ---- Sales/admin mutations (M3) ---- */

export async function updateStatus(number, { status, note } = {}) {
  const session = getSession();
  if (!session || !["sales", "manager", "admin"].includes(session.role)) {
    return withError("Sales role required", 403);
  }
  const valid = ["placed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];
  if (!valid.includes(status)) return withError("Invalid status", 400);
  const all = readOrders();
  const idx = all.findIndex((o) => o.number === number);
  if (idx === -1) return withError(`Order ${number} not found`, 404);

  // Recompute placed_at offset so derived timeline aligns with the new manual stage.
  // We "rewind" placed_at to make the order appear at the target stage.
  const stageOffsetMin = { placed: 0, packed: 2, shipped: 10, out_for_delivery: 60, delivered: 360 };
  const offset = stageOffsetMin[status] ?? 0;
  const forcedPlacedAt = status === "cancelled"
    ? all[idx].placed_at
    : new Date(Date.now() - (offset + 0.5) * 60000).toISOString();
  const isCancel = status === "cancelled";

  const internal_notes = (all[idx].internal_notes || []).slice();
  if (note && note.trim()) {
    internal_notes.unshift({
      id: "n-" + Math.random().toString(36).slice(2, 8),
      author: session.email,
      role: session.role,
      message: note.trim(),
      ts: new Date().toISOString(),
    });
  }

  const updated = {
    ...all[idx],
    placed_at: isCancel ? all[idx].placed_at : forcedPlacedAt,
    cancelled_at: isCancel ? new Date().toISOString() : all[idx].cancelled_at,
    internal_notes,
  };
  all[idx] = updated;
  writeOrders(all);
  pushAudit({ actor_id: session.user_id, actor_role: session.role, action: "order.status_changed", target_type: "order", target_id: number, detail: status });
  return withLatency(decorate(updated));
}

export async function addNote(number, { message } = {}) {
  const session = getSession();
  if (!session || !["sales", "manager", "admin"].includes(session.role)) {
    return withError("Sales role required", 403);
  }
  if (!message || !message.trim()) return withError("Message required", 400);
  const all = readOrders();
  const idx = all.findIndex((o) => o.number === number);
  if (idx === -1) return withError(`Order ${number} not found`, 404);
  const note = {
    id: "n-" + Math.random().toString(36).slice(2, 8),
    author: session.email,
    role: session.role,
    message: message.trim(),
    ts: new Date().toISOString(),
  };
  const updated = {
    ...all[idx],
    internal_notes: [note, ...(all[idx].internal_notes || [])],
  };
  all[idx] = updated;
  writeOrders(all);
  pushAudit({ actor_id: session.user_id, actor_role: session.role, action: "order.note_added", target_type: "order", target_id: number });
  return withLatency(decorate(updated));
}
